import { Icon } from '@capsico/ui';
import type { CountryCode, CustomerDish } from '@capsico/contracts';
import { money } from '../utils/money';
import { VegMark } from './ui';
import { Thumb } from './Reviews';

/**
 * C2 dish row (food adaptation of pattern H). Veg / non-veg mark, Bestseller tag, name 15/600,
 * price, 2-line description, "Customisable" caption. Photo right with ADD over its bottom edge;
 * no photo keeps ADD on the right. Sold out = greyed, ADD hidden, "Next available …" shown.
 * Fixed height 148 so long menus can be windowed.
 */
export const DISH_ROW_HEIGHT = 148;

export function DishCard({
  dish,
  country,
  labels,
  disabled,
  quantity = 0,
  onAdd,
  onRemove,
}: {
  dish: CustomerDish;
  country: CountryCode | null;
  labels: {
    bestseller: string;
    customisable: string;
    add: string;
    /** "24 liked" (W17 dish likes, last 90 days). */
    likes?: (count: number) => string;
  };
  disabled: boolean;
  /** How many are in the cart: ADD turns into a − n + stepper. */
  quantity?: number;
  onAdd: () => void;
  onRemove?: () => void;
}) {
  const soldOut = !dish.available;
  return (
    <div className="flex gap-3 px-4 py-4" style={{ height: DISH_ROW_HEIGHT }}>
      <div className={`min-w-0 flex-1 ${soldOut ? 'opacity-60' : ''}`}>
        <div className="flex items-center gap-2">
          <VegMark type={dish.foodType} size={14} />
          {dish.isBestseller && (
            <span className="rounded bg-warning-bg px-1.5 text-micro font-semibold text-warning">
              {labels.bestseller}
            </span>
          )}
          {labels.likes && dish.likes !== undefined && dish.likes > 0 && (
            <span className="inline-flex items-center gap-0.5 text-micro font-medium text-positive tabular">
              <Thumb size={11} />
              {labels.likes(dish.likes)}
            </span>
          )}
        </div>
        <h3 className="mt-1 line-clamp-1 text-title font-semibold text-ink">{dish.name}</h3>
        <p className="text-sm font-semibold text-ink tabular">{money(country, dish.priceMinor)}</p>
        {soldOut ? (
          <p className="mt-1 text-xs font-medium text-negative">{dish.unavailableText}</p>
        ) : (
          dish.description && (
            <p className="mt-1 line-clamp-2 text-xs text-ink-2">{dish.description}</p>
          )
        )}
      </div>
      <div className="relative flex w-28 shrink-0 flex-col items-center">
        {dish.photo ? (
          <img
            src={dish.photo.smallUrl}
            alt=""
            loading="lazy"
            className={`size-28 rounded-xl object-cover ${soldOut ? 'grayscale' : ''}`}
          />
        ) : (
          <div className="h-10" />
        )}
        {!soldOut && (
          <div className={dish.photo ? '-mt-5' : ''}>
            {quantity > 0 && onRemove ? (
              <div className="flex h-9 w-24 items-center justify-between rounded-lg bg-brand px-1 text-on-brand shadow-[0_2px_6px_rgba(15,23,42,0.08)]">
                <button
                  type="button"
                  aria-label={`− ${dish.name}`}
                  onClick={onRemove}
                  className="flex size-7 items-center justify-center"
                >
                  <Icon name="minus" size={14} />
                </button>
                <span className="text-sm font-bold tabular">{quantity}</span>
                <button
                  type="button"
                  aria-label={`+ ${dish.name}`}
                  disabled={disabled}
                  onClick={onAdd}
                  className="flex size-7 items-center justify-center disabled:opacity-50"
                >
                  <Icon name="plus" size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={disabled}
                onClick={onAdd}
                className="h-9 w-24 rounded-lg bg-surface text-sm font-bold text-brand-ink shadow-[inset_0_0_0_1px_var(--color-control),0_2px_6px_rgba(15,23,42,0.08)] active:scale-[0.97] disabled:text-ink-3"
              >
                {labels.add}
              </button>
            )}
            {dish.customisable && (
              <p className="mt-1 text-center text-micro text-ink-3">{labels.customisable}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
